import type { Olympus } from '@owner/olympus-client'
import { storeToRefs } from 'pinia'
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  toRaw,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue'

import { useAuthStore } from '@/auth/useAuthStore'
import { VITE_OLYMPUS_URL } from '@/OlympusClient'

import {
  consumeHestiaPreviewOverrideFromQuery,
  getStoredHestiaPreviewOverride,
} from './hestiaPreviewOverride'

export type EmbedMode =
  | 'customers'
  | 'agent-hermes'
  | 'launch-hermes'
  | 'website-builder'
  | 'website-builder-hermes'

export interface HestiaParentViewport {
  height: number
  width: number
}

interface HestiaIframeExtraData {
  customers?: {
    scope: 'brand'
  }
  launchCta?: {
    href: string
    isVisible: boolean
    label: string
  }
  parentViewport?: HestiaParentViewport
}

interface HestiaIframeSessionData {
  apiUrl: string
  audience: 'hermes'
  brandId?: string
  locationId?: string
  csrfToken: string
  embed?: EmbedMode
  extraData?: HestiaIframeExtraData
  isMultiBrandUser: boolean
  user: Olympus.Users.User
}

interface UseHestiaIframeOptions {
  brandId: Ref<string | undefined>
  embed?: EmbedMode
  extraData?: Ref<HestiaIframeExtraData | undefined>
  hestiaUrl?: string
  locationId?: Ref<string | undefined>
  useScopedAuthRoute?: boolean
}

interface UseHestiaIframeReturn {
  error: Ref<string | null>
  iframeKey: Ref<number>
  iframeRef: Ref<HTMLIFrameElement | null>
  iframeSrc: ComputedRef<string>
  isReady: Ref<boolean>
}

export const getHestiaParentViewport = (): HestiaParentViewport | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }

  return {
    height: window.innerHeight,
    width: window.innerWidth,
  }
}

export const mergeHestiaExtraDataWithParentViewport = (
  extraData: HestiaIframeExtraData | undefined,
  parentViewport: HestiaParentViewport | undefined
): HestiaIframeExtraData | undefined => {
  if (!extraData && !parentViewport) {
    return undefined
  }

  return {
    ...extraData,
    parentViewport,
  }
}

export const buildHestiaParentViewportMessage = (
  parentViewport: HestiaParentViewport
): {
  type: 'HESTIA_PARENT_VIEWPORT_UPDATED'
  viewport: HestiaParentViewport
} => ({
  type: 'HESTIA_PARENT_VIEWPORT_UPDATED',
  viewport: parentViewport,
})

export const toHestiaPostMessagePayload = <Payload extends object>(
  payload: Payload
): Payload => JSON.parse(JSON.stringify(toRaw(payload))) as Payload

export const getHestiaUrl = (overrideUrl?: string): string => {
  const queryOverrideUrl = consumeHestiaPreviewOverrideFromQuery()
  const storedOverrideUrl = getStoredHestiaPreviewOverride()
  const url =
    queryOverrideUrl ??
    storedOverrideUrl ??
    overrideUrl ??
    import.meta.env.VITE_HESTIA_URL

  if (!url) {
    return 'http://localhost:8082'
  }
  return url
}

interface BuildHestiaIframeAuthUrlOptions {
  brandId?: string
  hestiaUrl: string
  locationId?: string
  useScopedAuthRoute?: boolean
}

export const buildHestiaIframeAuthUrl = (
  options: BuildHestiaIframeAuthUrlOptions
): string => {
  const { brandId, hestiaUrl, locationId, useScopedAuthRoute } = options

  if (useScopedAuthRoute && brandId && locationId) {
    return new URL(
      `/${brandId}/${locationId}/iframe-auth`,
      hestiaUrl
    ).toString()
  }

  return new URL('/iframe-auth', hestiaUrl).toString()
}

export const useHestiaIframe = (
  options: UseHestiaIframeOptions
): UseHestiaIframeReturn => {
  const {
    brandId,
    embed,
    extraData,
    hestiaUrl: providedHestiaUrl,
    locationId,
    useScopedAuthRoute,
  } = options
  const authStore = useAuthStore()
  const { isMultiBrandUser } = storeToRefs(authStore)

  const iframeRef = ref<HTMLIFrameElement | null>(null)
  const iframeReady = ref(false)
  const iframeOrigin = ref<string | null>(null)
  const sessionSent = ref(false)
  const error = ref<string | null>(null)
  const iframeKey = ref(0)
  const parentViewport = ref<HestiaParentViewport | undefined>(
    getHestiaParentViewport()
  )

  const hestiaUrl = getHestiaUrl(providedHestiaUrl)
  const expectedOrigin = new URL(hestiaUrl).origin
  const shouldSendParentViewport = embed === 'customers'

  const hestiaSession = computed<HestiaIframeSessionData | null>(() => {
    if (!authStore.csrfToken || !authStore.user) {
      return null
    }
    return {
      apiUrl: VITE_OLYMPUS_URL,
      audience: 'hermes',
      brandId: brandId.value,
      csrfToken: authStore.csrfToken,
      embed,
      extraData: mergeHestiaExtraDataWithParentViewport(
        extraData?.value,
        shouldSendParentViewport ? parentViewport.value : undefined
      ),
      isMultiBrandUser: isMultiBrandUser.value,
      locationId: locationId?.value,
      user: toRaw(authStore.user),
    }
  })

  const sendParentViewportToHestia = () => {
    if (
      !shouldSendParentViewport ||
      !iframeRef.value?.contentWindow ||
      !iframeOrigin.value ||
      !parentViewport.value
    ) {
      return
    }

    const message = toHestiaPostMessagePayload(
      buildHestiaParentViewportMessage(parentViewport.value)
    )

    iframeRef.value.contentWindow.postMessage(message, iframeOrigin.value)
  }

  const sendSessionToHestia = () => {
    if (
      sessionSent.value ||
      !iframeRef.value?.contentWindow ||
      !iframeOrigin.value ||
      !hestiaSession.value
    ) {
      return
    }

    const message = toHestiaPostMessagePayload({
      session: hestiaSession.value,
      type: 'HESTIA_SESSION_HYDRATION',
    })

    iframeRef.value.contentWindow.postMessage(message, iframeOrigin.value)

    sessionSent.value = true
  }

  const handleMessage = (event: MessageEvent) => {
    if (event.origin !== expectedOrigin) {
      return
    }

    if (event.data?.type === 'HESTIA_IFRAME_READY') {
      iframeOrigin.value = event.origin
      iframeReady.value = true
    }

    if (event.data?.type === 'HESTIA_SESSION_HYDRATION_ERROR') {
      error.value = event.data.error ?? 'Failed to hydrate session'
    }
  }

  const handleResize = () => {
    parentViewport.value = getHestiaParentViewport()
    sendParentViewportToHestia()
  }

  watch(
    [brandId, () => locationId?.value, () => extraData?.value],
    () => {
      iframeReady.value = false
      sessionSent.value = false
      iframeOrigin.value = null
      error.value = null
      iframeKey.value++
    },
    { deep: true }
  )

  watch([iframeReady, hestiaSession, sessionSent], ([ready, sess, sent]) => {
    if (ready && sess && !sent) {
      sendSessionToHestia()
    }
  })

  onMounted(() => {
    window.addEventListener('message', handleMessage)
    window.addEventListener('resize', handleResize)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('message', handleMessage)
    window.removeEventListener('resize', handleResize)
  })

  const iframeSrc = computed(() => {
    return buildHestiaIframeAuthUrl({
      brandId: brandId.value,
      hestiaUrl,
      locationId: locationId?.value,
      useScopedAuthRoute,
    })
  })

  return {
    error,
    iframeKey,
    iframeRef,
    iframeSrc,
    isReady: iframeReady,
  }
}
